import React, { useRef, useEffect, useState } from 'react';
import { FaRegCopy, FaCheckCircle } from 'react-icons/fa';

const OTPInput = ({ 
  length = 6, 
  value = [], 
  onChange, 
  onComplete = null,
  disabled = false,
  autoFocus = true,
  className = "",
  showTimer = false,
  expiryTime = null,
  onResend = null,
  resendAttempts = 0,
  maxResendAttempts = 3
}) => {
  const inputRefs = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  // Timer countdown
  useEffect(() => {
    if (!showTimer || !expiryTime) return;
    
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
      setTimeLeft(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryTime, showTimer]);

  // Auto-focus first input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus, disabled]);

  // Auto-focus next empty input
  useEffect(() => {
    const nextEmptyIndex = value.findIndex(digit => digit === '');
    if (nextEmptyIndex !== -1 && nextEmptyIndex < length) {
      inputRefs.current[nextEmptyIndex]?.focus();
      setFocusedIndex(nextEmptyIndex);
    }
  }, [value, length]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (index, inputValue) => {
    // Only allow digits
    if (!/^\d?$/.test(inputValue)) return;

    const newValue = [...value];
    newValue[index] = inputValue;
    onChange(newValue);

    // Auto-advance to next input
    if (inputValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }

    // Check if all digits are filled
    if (newValue.every(digit => digit !== '') && onComplete) {
      setTimeout(() => onComplete(newValue.join('')), 300);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      } else if (value[index]) {
        // Clear current input
        const newValue = [...value];
        newValue[index] = '';
        onChange(newValue);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setFocusedIndex(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    if (/^\d+$/.test(pastedData) && pastedData.length <= length) {
      const newValue = [...value];
      const digits = pastedData.split('');
      
      // Fill available slots
      for (let i = 0; i < Math.min(digits.length, length); i++) {
        newValue[i] = digits[i];
      }
      
      onChange(newValue);
      
      // Focus the next empty input or the last one
      const nextEmptyIndex = newValue.findIndex(digit => digit === '');
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : length - 1;
      inputRefs.current[focusIndex]?.focus();
      setFocusedIndex(focusIndex);
    }
  };

  const handleInputFocus = (index) => {
    setFocusedIndex(index);
  };

  const handleInputClick = (index) => {
    inputRefs.current[index]?.focus();
    setFocusedIndex(index);
  };

  const copyToClipboard = () => {
    const otp = value.join('');
    if (otp.length === length) {
      navigator.clipboard.writeText(otp);
      // You could add a toast notification here
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* OTP Input Fields */}
      <div className="flex justify-center gap-3">
        {Array.from({ length }, (_, index) => (
          <div key={index} className="relative">
            <input
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              maxLength={1}
              value={value[index] || ''}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => handleInputFocus(index)}
              onClick={() => handleInputClick(index)}
              disabled={disabled}
              className={`
                h-14 w-14 rounded-xl border-2 text-center text-2xl font-bold 
                transition-all duration-200 focus:outline-none
                ${value[index]
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                  : focusedIndex === index
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                  : 'border-gray-200 dark:border-navy-700 dark:bg-navy-900 text-navy-700 dark:text-white'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}
                focus:ring-2 focus:ring-orange-500/20
              `}
              aria-label={`OTP digit ${index + 1}`}
            />
            
            {/* Focus indicator */}
            {focusedIndex === index && !disabled && (
              <div className="absolute inset-0 rounded-xl ring-2 ring-orange-500/30 animate-pulse" />
            )}
            
            {/* Success indicator */}
            {value[index] && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            )}
          </div>
        ))}
      </div>

      {/* Timer and Copy */}
      <div className="flex items-center justify-between text-sm">
        {showTimer && expiryTime && (
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
            <span>Expires in: <span className="font-mono font-semibold text-orange-600">{formatTime(timeLeft)}</span></span>
          </div>
        )}
        
        {value.every(digit => digit !== '') && (
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-2 text-orange-500 hover:text-orange-600 transition-colors"
            title="Copy OTP to clipboard"
          >
            <FaRegCopy className="h-4 w-4" />
            <span>Copy OTP</span>
          </button>
        )}
      </div>

      {/* Resend Section */}
      {onResend && (
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Didn't receive the code?
          </p>
          <button
            onClick={onResend}
            disabled={resendAttempts >= maxResendAttempts || disabled}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
              ${resendAttempts >= maxResendAttempts || disabled
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700'
              }
            `}
          >
            {resendAttempts >= maxResendAttempts 
              ? `Max attempts reached (${maxResendAttempts})`
              : `Resend code (${maxResendAttempts - resendAttempts} left)`
            }
          </button>
        </div>
      )}

      {/* Help Text */}
      <div className="text-center text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p>• Enter the 6-digit code sent to your phone</p>
        <p>• You can paste the entire code at once</p>
        <p>• Use arrow keys to navigate between fields</p>
      </div>
    </div>
  );
};

export default OTPInput; 